import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { lazy, Suspense, useEffect, useRef } from "react";
import { useAuthContext } from "@charphq/ui/providers/auth-provider";
import { isSafeRedirectUrl } from "@charphq/ui/lib/utils";
import { authClient } from "../lib/auth-client";
import { invalidateAuthCache } from "../lib/auth-guards";

// Lazy load auth components for better code splitting
const AuthPage = lazy(() =>
  import("@charphq/ui/components/auth").then((module) => ({ default: module.AuthPage })),
);

/**
 * True when this page was reached from the OAuth 2.1 authorize endpoint (MCP
 * clients). The signed query (`sig`, `client_id`, …) must stay in the URL —
 * the auth client forwards it on sign-in so the server resumes the flow.
 */
function isOAuthAuthorizeFlow(): boolean {
  if (typeof window === "undefined") return false;
  const params = new URLSearchParams(window.location.search);
  return params.has("sig") && params.has("client_id");
}

export const Route = createFileRoute("/auth")({
  beforeLoad: async () => {
    // If already authenticated, redirect to dashboard
    // This will be handled by the component
  },
  component: function Auth() {
    const { isAuthenticated, isLoading } = useAuthContext();
    const navigate = useNavigate();
    const oauthFlow = isOAuthAuthorizeFlow();
    // Fire the redirect once. StrictMode double-invokes this mount effect, and a
    // second navigate() to the code-split /dashboard route aborts the first
    // chunk import and hangs it pending — so guard with a ref that survives the
    // setup → cleanup → setup double-invoke.
    const didRedirect = useRef(false);

    // Redirect already-authenticated users in an effect — never during render
    // (a render-time navigate() loops as "Maximum update depth exceeded").
    // In an OAuth authorize flow the user was sent here deliberately (e.g.
    // prompt=login) — don't bounce them to the dashboard.
    useEffect(() => {
      if (oauthFlow || isLoading || !isAuthenticated || didRedirect.current) return;
      didRedirect.current = true;
      navigate({ to: "/dashboard", replace: true });
    }, [oauthFlow, isLoading, isAuthenticated, navigate]);

    // Show loading while auth resolves, or while the redirect above is in flight.
    if (!oauthFlow && (isLoading || isAuthenticated)) {
      return (
        <div className="flex min-h-screen items-center justify-center bg-background">
          <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-primary"></div>
        </div>
      );
    }

    return (
      <Suspense
        fallback={
          <div className="flex min-h-screen items-center justify-center bg-background">
            <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-primary"></div>
          </div>
        }
      >
        <AuthPage
          mode="signin"
          onSuccess={(result) => {
            // OAuth authorize flow: the sign-in response carries the URL that
            // resumes the flow (consent page or the client's redirect_uri).
            // Only follow it when it's a safe http/https target — never assign an
            // unvalidated (e.g. `javascript:`) URL to window.location.href.
            const data = result?.data;
            if (data?.redirect && isSafeRedirectUrl(data.url)) {
              didRedirect.current = true;
              window.location.href = data.url;
              return;
            }
            // Drop the stale signed-out verdict so the root gate sees the new
            // session immediately and doesn't bounce us back to /auth.
            invalidateAuthCache();
            // Successfully signed in, redirect to dashboard
            // Use router navigation instead of window.location
            navigate({ to: "/dashboard" });
          }}
          authClient={authClient}
        />
      </Suspense>
    );
  },
});
